import { getAuth } from 'firebase/auth';
import type {
  ContinuityCheckResult,
  MissingChapterContext,
  NeighborChapterProse,
} from '../types/missingChapterContext';
import type { RecoveryCandidate, RecoveryCandidatePreview } from '../types/recoveryCandidate';
import type { RecoveryContract, ValidationReport } from '../types';
import { normalizeMissingChapterContext } from './recoveryContextPresentation';
import { isFirebaseMockModeEnabled } from './firebaseMockMode';

async function recoveryPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const auth = getAuth();
  let token = await auth.currentUser?.getIdToken();
  if (!token && isFirebaseMockModeEnabled()) {
    token = 'mock_user_token';
  }
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    throw new Error(`Request failed (HTTP ${response.status}).`);
  }

  if (!response.ok) {
    throw new Error((data.error as string) || `Request failed (HTTP ${response.status}).`);
  }

  return data as T;
}

export async function fetchMissingChapterContext(
  projectId: string,
  missingChapterNumber: number,
): Promise<MissingChapterContext> {
  const raw = await recoveryPost<Partial<MissingChapterContext>>('/api/recovery/context', {
    projectId,
    missingChapterNumber,
  });
  return normalizeMissingChapterContext(raw).context;
}

export async function fetchMissingChapterContextWithMeta(
  projectId: string,
  missingChapterNumber: number,
) {
  const raw = await recoveryPost<Partial<MissingChapterContext>>('/api/recovery/context', {
    projectId,
    missingChapterNumber,
  });
  return normalizeMissingChapterContext(raw);
}

export async function fetchNeighborChapterProse(
  projectId: string,
  missingChapterNumber: number,
  chapterNumber: number,
): Promise<NeighborChapterProse> {
  return recoveryPost<NeighborChapterProse>('/api/recovery/neighbor-chapter', {
    projectId,
    missingChapterNumber,
    chapterNumber,
  });
}

export async function checkRestoreContinuity(
  projectId: string,
  missingChapterNumber: number,
  title: string,
  content: string,
): Promise<ContinuityCheckResult> {
  return recoveryPost<ContinuityCheckResult>('/api/recovery/check-continuity', {
    projectId,
    missingChapterNumber,
    title,
    content,
  });
}

export async function searchSavedCopies(
  projectId: string,
  missingChapterNumber: number,
): Promise<RecoveryCandidate[]> {
  const data = await recoveryPost<{ candidates: RecoveryCandidate[] }>('/api/recovery/search', {
    projectId,
    missingChapterNumber,
  });
  return data.candidates ?? [];
}

export async function previewRecoveryCandidate(
  projectId: string,
  missingChapterNumber: number,
  candidateId: string,
): Promise<RecoveryCandidatePreview> {
  return recoveryPost<RecoveryCandidatePreview>('/api/recovery/candidate-preview', {
    projectId,
    missingChapterNumber,
    candidateId,
  });
}

export async function restoreRecoveryCandidate(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  candidateId: string,
) {
  if (!projectId?.trim()) throw new Error('Project ID is required.');
  if (!recoveryOperationId?.trim()) throw new Error('Recovery operation ID is missing.');
  if (!candidateId?.trim()) throw new Error('Candidate ID is missing.');

  console.log('[RECOVERY UI] Request started', {
    projectId,
    chapterNumber: missingChapterNumber,
    operationId: recoveryOperationId,
    candidateRestore: true,
  });

  const data = await recoveryPost<{ status: string; message?: string; reusedStorage?: boolean }>(
    '/api/recovery/restore-candidate',
    {
      projectId,
      missingChapterNumber,
      recoveryOperationId,
      candidateId,
    },
  );

  console.log('[RECOVERY UI] Response received: success', {
    projectId,
    chapterNumber: missingChapterNumber,
    operationId: recoveryOperationId,
  });

  return data;
}

export async function restoreRecoveredChapter(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  recoveryType: "ORIGINAL_RESTORED" | "CACHE_RESTORED" | "EXPORT_RESTORED" | "RECONSTRUCTED_REPLACEMENT",
  title: string,
  content: string,
  sourceDescription: string
) {
  if (!projectId?.trim()) {
    throw new Error('Project ID is required to restore a chapter.');
  }
  if (!missingChapterNumber || missingChapterNumber < 1) {
    throw new Error('A valid chapter number is required.');
  }
  if (!recoveryOperationId?.trim()) {
    throw new Error('Recovery operation ID is missing.');
  }
  if (!title?.trim()) {
    throw new Error('Chapter title is required.');
  }
  if (!content?.trim()) {
    throw new Error('Chapter content is required.');
  }

  const auth = getAuth();
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("Not authenticated. Sign in and try again.");

  console.log('[RECOVERY UI] Request started', {
    projectId,
    chapterNumber: missingChapterNumber,
    operationId: recoveryOperationId,
  });

  const response = await fetch('/api/recovery/restore', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      projectId,
      missingChapterNumber,
      recoveryOperationId,
      recoveryType,
      title: title.trim(),
      content,
      sourceDescription
    })
  });

  console.log('[RECOVERY UI] Response received:', response.status, {
    projectId,
    chapterNumber: missingChapterNumber,
    operationId: recoveryOperationId,
  });

  let data: { error?: string; status?: string; message?: string } = {};
  try {
    data = await response.json();
  } catch {
    throw new Error(`Restore failed (HTTP ${response.status}).`);
  }

  if (!response.ok) {
    throw new Error(data.error || `Restore failed (HTTP ${response.status}).`);
  }

  return data;
}

export async function compileRecoveryContractClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
) {
  return recoveryPost<{
    contract: RecoveryContract;
    recoveryOperationId: string;
    status: string;
  }>('/api/recovery/contract', { projectId, missingChapterNumber, recoveryOperationId });
}

export async function saveRecoveryContractClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  contract: RecoveryContract,
) {
  return recoveryPost<{ contract: RecoveryContract; status: string }>(
    '/api/recovery/save-contract',
    { projectId, missingChapterNumber, recoveryOperationId, contract },
  );
}

export async function approveRecoveryContractClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  contract: RecoveryContract,
) {
  return recoveryPost<{
    contract: RecoveryContract;
    contractHash: string;
    status: string;
  }>('/api/recovery/approve-contract', {
    projectId,
    missingChapterNumber,
    recoveryOperationId,
    contract,
  });
}

export async function reconstructChapterClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  options?: { contractHash?: string; forceRegenerate?: boolean },
) {
  return recoveryPost<{
    prose: string;
    title: string;
    wordCount: number;
    status: string;
    reusedProse?: boolean;
  }>('/api/recovery/reconstruct', {
    projectId,
    missingChapterNumber,
    recoveryOperationId,
    contractHash: options?.contractHash,
    forceRegenerate: options?.forceRegenerate,
  });
}

export async function validateReconstructedProseClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
  contractHash?: string,
) {
  return recoveryPost<{
    validation: ValidationReport;
    status: string;
  }>('/api/recovery/validate', {
    projectId,
    missingChapterNumber,
    recoveryOperationId,
    contractHash,
  });
}

export async function cancelRecoveryOperationClient(projectId: string, recoveryOperationId: string) {
  return recoveryPost<{ status: string }>('/api/recovery/cancel', { projectId, recoveryOperationId });
}

export async function fetchRecoveryOperationClient(projectId: string, recoveryOperationId: string) {
  return recoveryPost<{
    status: string;
    contract?: RecoveryContract;
    contractHash?: string;
    contractVersion?: number;
    approvedByUid?: string;
    generatedTitle?: string;
    proseWordCount?: number;
    hasProse?: boolean;
    validationResult?: ValidationReport;
    lastError?: string;
  }>('/api/recovery/operation', { projectId, recoveryOperationId });
}

export async function approveReconstructionClient(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
) {
  return recoveryPost<{
    status: string;
    finalApproval: {
      approvedByUid: string;
      proseHash: string;
      validationHash: string;
      contractHash: string;
      userApproved: boolean;
    };
  }>('/api/recovery/approve-reconstruction', {
    projectId,
    missingChapterNumber,
    recoveryOperationId,
  });
}

export async function restoreReconstructedChapter(
  projectId: string,
  missingChapterNumber: number,
  recoveryOperationId: string,
) {
  return recoveryPost<{ status: string; message?: string; idempotentReplay?: boolean }>(
    '/api/recovery/restore',
    {
      projectId,
      missingChapterNumber,
      recoveryOperationId,
      recoveryType: 'RECONSTRUCTED_REPLACEMENT',
    },
  );
}
